import { types } from '@sunwisesoftware/sunwise-template-core';
import i18next from 'i18next';
import { isEmpty } from 'lodash';

import { URL_TYPES } from 'common/constants';
import { formatDate, formatPeriodLabel, parseDate } from 'common/utils/dates';
import {
    downloadFile,
    getFileExtensionByPath,
    getFileNameByPath,
    handleDownloadSunwiseFile,
} from 'common/utils/helpers';

import {
    CSV_FILE,
    DATASHEET_PDF_TYPE,
    ELECTRIC_BILL_TYPE,
    FINANCING_TYPE,
    PDF_HIGHT_QUALITY,
    PDF_LOW_QUALITY,
    PDF_MEDIUM_QUALITY,
} from './constants';

export const getDownloadOptions = (selectedDocument) => {
    const { archive, format, type, urlType } = selectedDocument;
    let options = [];

    const pdfOptions = [
        {
            label: i18next.t('PDF - {{quality}}', {
                quality: i18next.t('Low quality'),
            }),
            value: PDF_LOW_QUALITY,
        },
        {
            label: i18next.t('PDF - {{quality}}', {
                quality: i18next.t('Medium quality'),
            }),
            value: PDF_MEDIUM_QUALITY,
        },
        {
            label: i18next.t('PDF - {{quality}}', {
                quality: i18next.t('High quality'),
            }),
            value: PDF_HIGHT_QUALITY,
        },
    ];

    switch (type) {
        case DATASHEET_PDF_TYPE:
            options = [
                {
                    label: i18next.t('{{type}} - File', { type: 'PDF' }),
                    value: PDF_MEDIUM_QUALITY,
                },
            ];
            break;
        case ELECTRIC_BILL_TYPE:
            if (urlType === URL_TYPES.WISE && format === 'png')
                options = [
                    {
                        label: i18next.t('{{type}} - File', {
                            type: i18next.t('Image'),
                        }),
                        value: PDF_MEDIUM_QUALITY,
                    },
                ];
            else
                options = [
                    {
                        label: i18next.t('{{type}} - File', { type: 'PDF' }),
                        value: PDF_MEDIUM_QUALITY,
                    },
                ];
            break;
        case FINANCING_TYPE:
            if (archive)
                options = [
                    {
                        label: i18next.t('{{type}} - File', { type: 'PDF' }),
                        value: PDF_MEDIUM_QUALITY,
                    },
                ];
            else options = pdfOptions;
            break;
        case types.ONE_PROPOSAL_TYPE:
        case types.SMART_DOCUMENTS_TYPE:
            options = [
                ...pdfOptions,
                {
                    label: i18next.t('{{type}} - File', { type: 'CSV' }),
                    value: CSV_FILE,
                },
            ];
            break;
        default:
            options = pdfOptions;
            break;
    }
    return [
        {
            disabled: true,
            label: i18next.t('Select a format '),
            value: '',
        },
        ...options,
    ];
};

export const getElectricBillPerMonths = ({ isBimonthly = false, lines }) => {
    if (lines && !isEmpty(lines)) {
        const filteredKwh = lines.filter((line) => line.url);

        return filteredKwh.map((item) => {
            const label = formatPeriodLabel({
                finalDate: parseDate(item.final_date, 'dd/MM/yyyy'),
                isBimonthly,
            });

            return {
                url: item.url,
                id: Math.random(),
                label: label,
                urlType: item.url_type,
            };
        });
    }
    return [];
};

export const getDatasheetLabel = (key) => {
    const labels = {
        accesories: i18next.t('Accessories'),
        additionals: i18next.t('Additionals'),
        batteries: i18next.t('Batteries'),
        inverters: i18next.t('Inverters'),
        panels: i18next.t('Panels'),
        structures: i18next.t('Structures'),
        workforces: i18next.t('Wrokforce and electrical materials'),
    };
    return labels[key];
};

export const getDatasheetsByType = (datasheets, type) => {
    const data = datasheets.map((item) => ({ ...item, type: item.type?.key }));
    return data.filter((datasheet) => datasheet.type === type);
};

export const getDatasheetTypes = (datasheets) => {
    const data = [...datasheets].map((item) => item.type?.key);
    return [...new Set(data)];
};

export const getFormattedDate = (date) => {
    if (date) {
        return formatDate(parseDate(date, 'yyyy/MM/dd HH:mm:ss'), 'dd/MM/yyyy');
    }
    return null;
};

export const getIsLoadingContextMessage = ({
    isFetchingCustomTemplate,
    isFetchingElectricBills,
    isFetchingFinancingTemplate,
    isFetchingInteractiveProposal,
    isFetchingInteractiveProposalContent,
    isFetchingSmartDocumentContent,
    loadingContext,
}) => {
    if (isEmpty(loadingContext)) return;
    if (
        isFetchingCustomTemplate ||
        isFetchingElectricBills ||
        isFetchingFinancingTemplate ||
        isFetchingInteractiveProposal ||
        isFetchingInteractiveProposalContent ||
        isFetchingSmartDocumentContent
    )
        loadingContext.openLoading(i18next.t('Loading'));
    else loadingContext.closeLoading();
};

export const handleClickDocumentItemBuild = (
    document,
    fetchSmartDocumentContent,
    fetchFinancingTemplate,
    initTemplate,
    resetTemplate,
    selectedDocument,
    setDownloadFormat,
    setSelectedDocument,
    swipeableDrawerRef,
    templateContent,
) => {
    const { id, type } = document;
    if (id !== selectedDocument.id) {
        resetTemplate();
        setSelectedDocument(document);
        setDownloadFormat(PDF_MEDIUM_QUALITY);
        switch (type) {
            case FINANCING_TYPE:
                fetchFinancingTemplate(id, initTemplate);
                break;
            case types.SMART_DOCUMENTS_TYPE:
                fetchSmartDocumentContent(id, initTemplate);
                break;
            default:
                initTemplate(JSON.parse(templateContent));
                break;
        }
    }
    handleCloseDrawer(swipeableDrawerRef);
};

export const handleClickDownloadBuild = ({
    downloadDatasheet,
    downloadFormat,
    downloadProposalCsv,
    loadingContext,
    renderPdfFile,
    selectedDocument,
}) => {
    const { archive, id, name, type } = selectedDocument;
    switch (type) {
        case DATASHEET_PDF_TYPE:
            return downloadDatasheet(
                getFileNameByPath(selectedDocument.archive),
                (text) => loadingContext.openLoading(text),
                () => loadingContext.closeLoading(),
            );
        case ELECTRIC_BILL_TYPE:
            if (getFileExtensionByPath(selectedDocument?.archive) === 'xml')
                return handleDownloadSunwiseFile(archive, name);
            return downloadFile(archive, name);
        case FINANCING_TYPE:
            if (archive) return downloadFile(archive, `${name}.pdf`);
            return renderPdfFile(
                id,
                name,
                () => loadingContext.openLoading(i18next.t('Downloading')),
                () => loadingContext.closeLoading(),
                type,
            );
        case types.ONE_PROPOSAL_TYPE: {
            if (downloadFormat === CSV_FILE)
                return downloadProposalCsv(id, name, loadingContext);
            return renderPdfFile(
                id,
                name,
                () => loadingContext.openLoading(i18next.t('Downloading')),
                () => loadingContext.closeLoading(),
            );
        }
        case types.SMART_DOCUMENTS_TYPE:
            return renderPdfFile(
                id,
                name,
                () => loadingContext.openLoading(i18next.t('Downloading')),
                () => loadingContext.closeLoading(),
                type,
            );
        default:
            return false;
    }
};

export const handleClickElectricBillBuild = (
    document,
    fetchWiseFile,
    fetchZapFile,
    loadingContext,
    setDownloadFormat,
    setSelectedDocument,
    swipeableDrawerRef,
) => {
    switch (document.urlType) {
        case URL_TYPES.WISE: {
            loadingContext.openLoading(i18next.t('Getting file').concat('...'));
            fetchWiseFile(document?.fileName, async (data) => {
                let archive = data.url;
                const format = getFileExtensionByPath(data.url);

                setSelectedDocument({ ...document, archive, format });
                loadingContext.closeLoading();
            });
            break;
        }
        case URL_TYPES.ZAP: {
            loadingContext.openLoading(i18next.t('Getting file').concat('...'));

            fetchZapFile(document?.fileName, async (data) => {
                let archive = data.url;
                const format = getFileExtensionByPath(data.url);

                setSelectedDocument({ ...document, archive, format });
                loadingContext.closeLoading();
            });
            break;
        }
        default:
            setSelectedDocument(document);
    }

    setDownloadFormat(PDF_MEDIUM_QUALITY);
    handleCloseDrawer(swipeableDrawerRef);
};

export const handleCloseDrawer = (swipeableDrawerRef) => {
    if (swipeableDrawerRef.current) {
        const { handleOpen } = swipeableDrawerRef.current;
        handleOpen(false);
    }
};

export const handleScrollTopSideBar = (sideBarRef) => {
    if (sideBarRef && sideBarRef.current) sideBarRef.current.scrollTop = 0;
};
